import { useCallback, useEffect, useRef, useState } from 'react';
import { LoadInfiniteHook, useGetDataHook } from '@bymason/api-hooks-base';
import {
  CreateProvisonSessionOption,
  GetProvisionSessionOptions,
  provisionClient,
  ProvisionSession,
} from '@bymason/controller-api';

export {
  CreateProvisonSessionOption,
  provisionClient,
  ProvisionQRCodeModel,
  GetProvisionSessionOptions,
} from '@bymason/controller-api';

/**
 * React hook that asynchronously creates a provision session.
 * @returns HookArray
 * @HookArray [0] post method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useCreateProvisionSession(clientToken: string) {
  const handleGetData = useCallback(
    (options: CreateProvisonSessionOption) => {
      return provisionClient.newSession(options, clientToken);
    },
    [clientToken]
  );
  return useGetDataHook(handleGetData);
}

/**
 * React hook that asynchronously retrives a provision sessiopn.
 * @returns HookArray
 * @HookArray [0] get method
 * @HookArray [1] result
 * @HookArray [2] error
 * @HookArray [3] busy flag
 * @HookArray [4] error code
 * @HookArray [5] error data
 * @HookArray [6] clearData method
 */
export function useGetProvisionSession(clientToken: string) {
  const handleGetData = useCallback(
    (options: GetProvisionSessionOptions, signal?: AbortSignal | null) => {
      return provisionClient.getSession(options, clientToken, signal);
    },
    [clientToken]
  );
  return useGetDataHook(handleGetData);
}

/**
 * React hook that loads paginated provision session "infinitely" via trigger,
 * for components that need to load more data on scroll or button click.
 * @returns HookArray
 * @HookArray [0] result
 * @HookArray [1] error
 * @HookArray [2] busy flag
 * @HookArray [3] page count
 * @HookArray [4] error code
 * @HookArray [5] clearData method
 */
export function useLoadInfiniteProvisionSession(
  clientToken: string,
  options: GetProvisionSessionOptions,
  loadMoreTrigger = false
) {
  const [getData, data, error, busy, errorCode, errorData, clearData] = useGetProvisionSession(clientToken);
  const [loadedData, setLoadedData] = useState<ProvisionSession>();
  const [hasMoreData, setHasMoreData] = useState(false);
  const [nextPageMarker, setNextPageMarker] = useState<string | undefined>();
  const blockTrigger = useRef(false);
  const pageCount = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const resetLoadedData = useCallback(() => {
    clearData();
    setHasMoreData(false);
    setNextPageMarker(undefined);
    blockTrigger.current = false;
    pageCount.current = 0;
    controller.current = new AbortController();
    getData(options, controller.current.signal);
  }, [clearData, getData, options]);

  // Fetch initial data
  useEffect(() => {
    if (clientToken) {
      if (controller.current && !controller.current.signal.aborted) {
        controller.current.abort();
      }
      resetLoadedData();
    }
    // must have client token as dep. if it changes we
    // need to reset the loaded data
  }, [resetLoadedData, clientToken, clearData, getData, options]);

  // Fetch more data if conditions are right
  useEffect(() => {
    if (loadMoreTrigger && hasMoreData && !blockTrigger.current) {
      getData({ ...options, after: nextPageMarker });
    }
  }, [getData, options, loadMoreTrigger, hasMoreData, nextPageMarker]);

  // Process fetched data into loaded data and set flags
  useEffect(() => {
    if (!error && !busy && data) {
      const currData = data.data;
      blockTrigger.current = true;
      pageCount.current++;

      setLoadedData((prevData) => {
        if (prevData?.devices) {
          if (prevData.devices.length <= 5000) {
            if (pageCount.current <= 1) {
              return currData;
            }
            return { ...prevData, devices: [...prevData.devices, ...(currData.devices ?? [])] };
          }
          return prevData;
        } else {
          return currData;
        }
      });
      setHasMoreData(currData && !!data?.nextPage);
      setNextPageMarker(data?.nextPage);
    }

    if (error) {
      setHasMoreData(false);
    }
  }, [busy, data, error]);

  // Trigger block makes sure only one get event is triggered until the
  // trigger is reset. For instance, A scroll bottom trigger doesn't reset
  // to false after the data loads,but eventually does reset with the next onScroll event.
  useEffect(() => {
    if (loadMoreTrigger === false) {
      blockTrigger.current = false;
    }
  });

  return [
    loadedData,
    error,
    busy,
    pageCount.current,
    errorCode,
    errorData,
    resetLoadedData,
  ] as LoadInfiniteHook<ProvisionSession>;
}
